/**
 * API Service for CRM Backend
 * Supports JWE encryption when enabled
 */
import axios from 'axios';
import { isEncryptionEnabled, encryptData, initializeEncryption } from './encryption';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Initialize encryption on module load
let encryptionInitialized = false;
const ensureEncryptionInit = async () => {
  if (!encryptionInitialized) {
    await initializeEncryption();
    encryptionInitialized = true;
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and encrypt data
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Encrypt request data if encryption is enabled and there's data to encrypt
    await ensureEncryptionInit();
    if (isEncryptionEnabled() && config.data && typeof config.data === 'object') {
      const encryptedData = await encryptData(config.data);
      config.data = encryptedData;
      config.headers['Content-Type'] = 'application/jose+json';
      config.headers['X-Encrypted'] = 'true';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for decryption and error handling
api.interceptors.response.use(
  async (response) => {
    // Check if response is encrypted
    if (response.headers['x-encrypted'] === 'true' || 
        response.headers['content-type']?.includes('application/jose+json')) {
      try {
        // Import decryption dynamically to avoid circular deps
        const { decryptData } = await import('./encryption');
        const decrypted = await decryptData(response.data, null);
        response.data = decrypted;
      } catch (e) {
        console.warn('Failed to decrypt response:', e);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, null, {
            params: { refresh_token: refreshToken }
          });
          
          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('crm_user');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  logout: () => api.post('/api/auth/logout'),
  refresh: (refreshToken) => api.post('/api/auth/refresh', null, { params: { refresh_token: refreshToken } }),
  me: () => api.get('/api/auth/me'),
  register: (userData) => api.post('/api/auth/register', userData),

  // Cookie-based SSO session (only needed when frontend and backend are same-origin)
  ssoMe: () => api.get('/api/sso-auth/me', { withCredentials: true }),
  ssoLogin: (token, provider = 'external') => api.post('/api/sso-auth/login', { token, provider }, { withCredentials: true }),
  ssoLogout: () => api.post('/api/sso-auth/logout', {}, { withCredentials: true }),
};

export const ssoAPI = {
  config: () => api.get('/api/sso/config'),
  updateConfig: (data) => api.patch('/api/sso/config', data),
  rotate: () => api.post('/api/sso/config/rotate'),
  logs: (limit = 50) => api.get('/api/sso/logs', { params: { limit } }),
  summary: () => api.get('/api/sso/logs/summary'),
};

export const activityAPI = {
  user: (userId) => api.get(`/api/activity/users/${userId}`),
};


// Users API
export const usersAPI = {
  list: (params = {}) => api.get('/api/users/', { params }),
  listAdmin: () => api.get('/api/users/admin/list'),  // For admin view with password info
  get: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users/', data),
  update: (id, data) => api.patch(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
  stats: () => api.get('/api/users/stats/count'),
  changePassword: (data) => api.post('/api/users/change-password', data),
};

// Teams API
export const teamsAPI = {
  list: () => api.get('/api/teams/'),
  get: (id) => api.get(`/api/teams/${id}`),
  create: (data) => api.post('/api/teams/', data),
  update: (id, data) => api.patch(`/api/teams/${id}`, data),
  delete: (id) => api.delete(`/api/teams/${id}`),
  members: (id) => api.get(`/api/teams/${id}/members`),
};

// Leads API
export const leadsAPI = {
  list: (params = {}) => api.get('/api/leads/', { params }),
  get: (id) => api.get(`/api/leads/${id}`),
  create: (data) => api.post('/api/leads/', data),
  update: (id, data) => api.patch(`/api/leads/${id}`, data),
  delete: (id, confirm = false) => api.delete(`/api/leads/${id}`, { params: { confirm } }),
  checkRelations: (id) => api.get(`/api/leads/${id}/check-relations`),
  activities: (id) => api.get(`/api/leads/${id}/activities`),
  addActivity: (id, data) => api.post(`/api/leads/${id}/activities`, data),
  reject: (id, reason) => api.post(`/api/leads/${id}/reject`, null, { params: { rejection_reason: reason } }),
  changeStatus: (id, data) => api.post(`/api/leads/${id}/change-status`, data),
  reassign: (id, newAssigneeId, notes) => api.post(`/api/leads/${id}/reassign`, null, { params: { new_assignee_id: newAssigneeId, notes } }),
  recordCallOutcome: (id, data) => api.post(`/api/leads/${id}/call-outcome`, data),
  markCallbackDone: (id) => api.patch(`/api/leads/${id}/callback-done`),
  getPendingCallbacks: () => api.get('/api/leads/todos/pending'),
  getChatHistory: (id, limit = 100) => api.get(`/api/leads/${id}/chat-history`, { params: { limit } }),
};

// Demos API
export const demosAPI = {
  list: (params = {}) => api.get('/api/demos/', { params }),
  get: (id) => api.get(`/api/demos/${id}`),
  create: (data) => api.post('/api/demos/', data),
  update: (id, data) => api.patch(`/api/demos/${id}`, data),
  delete: (id) => api.delete(`/api/demos/${id}`),
  calendar: (month, year) => api.get('/api/demos/calendar', { params: { month, year } }),
  markSQD: (id, notes) => api.post(`/api/demos/${id}/mark-sqd`, null, { params: { sqd_notes: notes } }),
  stats: () => api.get('/api/demos/stats/summary'),
  reschedule: (id, data) => api.post(`/api/demos/${id}/reschedule`, data),
  complete: (id, data) => api.post(`/api/demos/${id}/complete`, data),
  availableSQD: () => api.get('/api/demos/available-sqd'),
  cancel: (id) => api.post(`/api/demos/${id}/cancel`),
};

// Deals API
export const dealsAPI = {
  list: (params = {}) => api.get('/api/deals/', { params }),
  get: (id) => api.get(`/api/deals/${id}`),
  create: (data) => api.post('/api/deals/', data),
  update: (id, data) => api.patch(`/api/deals/${id}`, data),
  delete: (id) => api.delete(`/api/deals/${id}`),
  approve: (id, stage) => api.post(`/api/deals/${id}/approve`, null, { params: { approval_stage: stage } }),
  pipeline: () => api.get('/api/deals/stats/pipeline'),
};

// Incentives API
export const incentivesAPI = {
  // Configs
  listConfigs: (params = {}) => api.get('/api/incentives/configs', { params }),
  getConfig: (id) => api.get(`/api/incentives/configs/${id}`),
  createConfig: (data) => api.post('/api/incentives/configs', data),
  updateConfig: (id, data) => api.patch(`/api/incentives/configs/${id}`, data),
  deleteConfig: (id) => api.delete(`/api/incentives/configs/${id}`),
  
  // Tiers
  listTiers: (params = {}) => api.get('/api/incentives/tiers', { params }),
  
  // SQD Slabs
  listSQDSlabs: (params = {}) => api.get('/api/incentives/sqd-slabs', { params }),
  createSQDSlab: (data) => api.post('/api/incentives/sqd-slabs', data),
  lockSQDSlab: (id) => api.patch(`/api/incentives/sqd-slabs/${id}/lock`),
  deleteSQDSlab: (id) => api.delete(`/api/incentives/sqd-slabs/${id}`),
  
  // Conversion Configs
  listConversionConfigs: () => api.get('/api/incentives/conversion-configs'),
  createConversionConfig: (data) => api.post('/api/incentives/conversion-configs', data),
  lockConversionConfig: (id) => api.patch(`/api/incentives/conversion-configs/${id}/lock`),
  deleteConversionConfig: (id) => api.delete(`/api/incentives/conversion-configs/${id}`),
  
  // Team Conversion Configs
  listTeamConfigs: () => api.get('/api/incentives/team-configs'),
  createTeamConfig: (data) => api.post('/api/incentives/team-configs', data),
  lockTeamConfig: (id) => api.patch(`/api/incentives/team-configs/${id}/lock`),
  deleteTeamConfig: (id) => api.delete(`/api/incentives/team-configs/${id}`),
  
  // Team SQD Configs (fixed amount per SQD to employee - legacy)
  listTeamSQDConfigs: () => api.get('/api/incentives/team-sqd-configs'),
  createTeamSQDConfig: (data) => api.post('/api/incentives/team-sqd-configs', data),
  lockTeamSQDConfig: (id) => api.patch(`/api/incentives/team-sqd-configs/${id}/lock`),
  deleteTeamSQDConfig: (id) => api.delete(`/api/incentives/team-sqd-configs/${id}`),
  
  // Team SQD Slabs (slab-based incentive to employee)
  listTeamSQDSlabs: (params = {}) => api.get('/api/incentives/team-sqd-slabs', { params }),
  createTeamSQDSlab: (data) => api.post('/api/incentives/team-sqd-slabs', data),
  deleteTeamSQDSlab: (id) => api.delete(`/api/incentives/team-sqd-slabs/${id}`),
  
  // Receivables (Company Income)
  listReceivables: (params = {}) => api.get('/api/incentives/receivables', { params }),
  createReceivable: (data) => api.post('/api/incentives/receivables', data),
  updateReceivable: (id, data) => api.patch(`/api/incentives/receivables/${id}`, data),
  receivableStats: () => api.get('/api/incentives/receivables/stats'),
  
  // Records
  listRecords: (params = {}) => api.get('/api/incentives/records', { params }),
  createRecord: (data) => api.post('/api/incentives/records', data),
  approveRecord: (id) => api.patch(`/api/incentives/records/${id}/approve`),
  markPayable: (id) => api.patch(`/api/incentives/records/${id}/mark-payable`),
  markPaid: (id, ref) => api.patch(`/api/incentives/records/${id}/mark-paid`, null, { params: { payment_reference: ref } }),
  stats: () => api.get('/api/incentives/stats/summary'),
};

// Invoices API
export const invoicesAPI = {
  list: (params = {}) => api.get('/api/invoices/', { params }),
  get: (id) => api.get(`/api/invoices/${id}`),
  create: (data) => api.post('/api/invoices/', data),
  update: (id, data) => api.patch(`/api/invoices/${id}`, data),
  delete: (id) => api.delete(`/api/invoices/${id}`),
  payouts: (params = {}) => api.get('/api/invoices/payouts', { params }),
  createPayout: (data) => api.post('/api/invoices/payouts', data),
  stats: () => api.get('/api/invoices/stats/summary'),
  updateStatus: (id, status) => api.patch(`/api/invoices/${id}/status`, { status }),
};

// Tags API
export const tagsAPI = {
  list: () => api.get('/api/tags/'),
  get: (id) => api.get(`/api/tags/${id}`),
  create: (data) => api.post('/api/tags/', data),
  update: (id, data) => api.patch(`/api/tags/${id}`, data),
  delete: (id) => api.delete(`/api/tags/${id}`),
};

// Webhooks API
export const webhooksAPI = {
  list: () => api.get('/api/webhooks/'),
  get: (id) => api.get(`/api/webhooks/${id}`),
  create: (data) => api.post('/api/webhooks/', data),
  update: (id, data) => api.patch(`/api/webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/webhooks/${id}`),
  toggle: (id) => api.patch(`/api/webhooks/${id}/toggle`),
  logs: (id, limit = 50) => api.get(`/api/webhooks/${id}/logs`, { params: { limit } }),
};

// Templates API
export const templatesAPI = {
  // WhatsApp
  listWhatsApp: () => api.get('/api/templates/whatsapp'),
  getWhatsApp: (id) => api.get(`/api/templates/whatsapp/${id}`),
  createWhatsApp: (data) => api.post('/api/templates/whatsapp', data),
  updateWhatsApp: (id, data) => api.patch(`/api/templates/whatsapp/${id}`, data),
  deleteWhatsApp: (id) => api.delete(`/api/templates/whatsapp/${id}`),
  
  // Email
  listEmail: () => api.get('/api/templates/email'),
  getEmail: (id) => api.get(`/api/templates/email/${id}`),
  createEmail: (data) => api.post('/api/templates/email', data),
  updateEmail: (id, data) => api.patch(`/api/templates/email/${id}`, data),
  deleteEmail: (id) => api.delete(`/api/templates/email/${id}`),
};

// Communication API
export const communicationAPI = {
  sendEmail: (data) => api.post('/api/communication/email', data),
  sendSMS: (data) => api.post('/api/communication/sms', data),
  sendWhatsApp: (data) => api.post('/api/communication/whatsapp', data),
  logs: (params = {}) => api.get('/api/communication/logs', { params }),
  getLog: (id) => api.get(`/api/communication/logs/${id}`),
};

// Plans API
export const plansAPI = {
  list: (activeOnly = true) => api.get('/api/plans/', { params: { active_only: activeOnly } }),
  get: (id) => api.get(`/api/plans/${id}`),
  create: (data) => api.post('/api/plans/', data),
  update: (id, data) => api.patch(`/api/plans/${id}`, data),
  delete: (id) => api.delete(`/api/plans/${id}`),
  calculate: (planId, userCount) => api.post('/api/plans/calculate', null, { params: { plan_id: planId, user_count: userCount } }),
};

// Dashboard API
export const dashboardAPI = {
  stats: () => api.get('/api/dashboard/stats'),
  monthlyPerformance: () => api.get('/api/dashboard/performance/monthly'),
  incentiveTrend: () => api.get('/api/dashboard/performance/incentives'),
  funnel: () => api.get('/api/dashboard/funnel'),
};

// Clients API
export const clientsAPI = {
  list: (params = {}) => api.get('/api/clients/', { params }),
  get: (id) => api.get(`/api/clients/${id}`),
  stats: () => api.get('/api/clients/stats/summary'),
  updatePlanAmount: (id, data) => api.patch(`/api/clients/${id}/plan-amount`, data),
  getTimeline: (id) => api.get(`/api/clients/${id}/timeline`),
};

// Lead Bank API
export const leadBankAPI = {
  list: (params = {}) => api.get('/api/lead-bank/', { params }),
  assign: (leadId, userId) => api.post(`/api/lead-bank/${leadId}/assign`, null, { params: { user_id: userId } }),
  bulkAssign: (leadIds, userId) => api.post('/api/lead-bank/bulk-assign', null, { params: { lead_ids: leadIds, user_id: userId } }),
  stats: () => api.get('/api/lead-bank/stats'),
};

// API Config API
export const apiConfigAPI = {
  list: () => api.get('/api/api-config/'),
  create: (data) => api.post('/api/api-config/', data),
  delete: (id) => api.delete(`/api/api-config/${id}`),
  toggle: (id) => api.patch(`/api/api-config/${id}/toggle`),
  getCurlExample: (id) => api.get(`/api/api-config/curl-example/${id}`),
};

// Outbound Webhooks API (for events)
export const outboundWebhooksAPI = {
  list: () => api.get('/api/outbound-webhooks/'),
  create: (data) => api.post('/api/outbound-webhooks/', data),
  get: (id) => api.get(`/api/outbound-webhooks/${id}`),
  update: (id, data) => api.patch(`/api/outbound-webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/outbound-webhooks/${id}`),
  toggle: (id) => api.post(`/api/outbound-webhooks/${id}/toggle`),
  test: (id) => api.post(`/api/outbound-webhooks/${id}/test`),
  getLogs: (id, limit = 50) => api.get(`/api/outbound-webhooks/${id}/logs`, { params: { limit } }),
  getEvents: () => api.get('/api/outbound-webhooks/events'),
};

// Email Config API
export const emailConfigAPI = {
  list: () => api.get('/api/email-config/'),
  create: (data) => api.post('/api/email-config/', data),
  get: (id) => api.get(`/api/email-config/${id}`),
  update: (id, data) => api.patch(`/api/email-config/${id}`, data),
  delete: (id) => api.delete(`/api/email-config/${id}`),
  toggle: (id) => api.post(`/api/email-config/${id}/toggle`),
  test: (id, email) => api.post(`/api/email-config/${id}/test`, null, { params: { test_email: email } }),
  getLogs: (id, limit = 50) => api.get(`/api/email-config/${id}/logs`, { params: { limit } }),
  getEvents: () => api.get('/api/email-config/notification-events'),
};

// Reports API
export const reportsAPI = {
  performance: (params = {}) => api.get('/api/reports/performance', { params }),
  monthlyTrend: (months = 6) => api.get('/api/reports/monthly-trend', { params: { months } }),
  incentiveSummary: (year, month) => api.get('/api/reports/incentive-summary', { params: { year, month } }),
  sourceAnalytics: () => api.get('/api/reports/source-analytics'),
};

// Notifications API
export const notificationsAPI = {
  list: (limit = 10) => api.get('/api/notifications/', { params: { limit } }),
  markRead: (id) => api.post(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
};

// Settings API
export const settingsAPI = {
  get: () => api.get('/api/settings/'),
  getSetting: (key) => api.get(`/api/settings/${key}`),
  update: (updates) => api.patch('/api/settings/', updates),
  updateSetting: (key, value) => api.put(`/api/settings/${key}`, { key, value }),
  getProfile: () => api.get('/api/settings/profile/me'),
  updateProfile: (preferences) => api.patch('/api/settings/profile/me', preferences),
};

export default api;
